<template>
  <div
    v-if="toasts.length"
    class="ash-toaster"
    :style="{ height: containerHeight + 'px' }"
    @mouseenter="pauseAll"
    @mouseleave="resumeAll"
  >
    <transition-group name="ash-toast" tag="div" class="ash-toaster-stack">
      <div
        v-for="(toast, index) in toasts"
        :key="toast.id"
        ref="items"
        :data-toast-id="toast.id"
        class="ash-toast"
        :style="itemStyle(index)"
      >
        <span class="ash-toast-icon" :class="'is-' + toast.variant" v-html="icons[toast.variant]"></span>
        <div class="ash-toast-content">
          <div v-if="toast.title" class="ash-toast-title">{{ toast.title }}</div>
          <div class="ash-toast-message" :class="{ 'no-title': !toast.title }" v-html="toast.message"></div>
        </div>
        <button class="ash-toast-close" aria-label="Dismiss" @click="dismiss(toast.id)">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    </transition-group>
  </div>
</template>

<script>
import { toastState, dismissToast } from "../../utils/toast";

const PEEK = 14; // px each collapsed toast peeks out below the front one
const GAP = 12; // px between toasts when expanded
const FALLBACK_HEIGHT = 60; // used until a toast's real height is measured
const MAX_VISIBLE = 3;

export default {
  name: "toaster",
  data() {
    return {
      expanded: false,
      heights: {},
    };
  },
  beforeCreate() {
    // timer bookkeeping kept off `data` — nothing in the template depends on it.
    // Must be beforeCreate: the `toasts` watcher is immediate, and Vue fires
    // immediate watchers during init, BEFORE created() — with this in created(),
    // the first fire hit Object.keys(undefined) and could abort the whole mount
    // (frozen review pages, found via PBC_Wraps validation 2026-08-24).
    this.timers = {};
  },
  computed: {
    toasts() {
      return toastState.toasts;
    },
    icons() {
      return {
        success:
          '<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="8" fill="currentColor"/><path d="M5.5 9.2l2.3 2.3 4.7-4.7" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        error:
          '<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="8" fill="currentColor"/><path d="M6.3 6.3l5.4 5.4M11.7 6.3l-5.4 5.4" stroke="#fff" stroke-width="1.6" stroke-linecap="round"/></svg>',
        warning:
          '<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 1.8L17 15.4H1L9 1.8z" fill="currentColor"/><path d="M9 7v3.4" stroke="#fff" stroke-width="1.6" stroke-linecap="round"/><circle cx="9" cy="12.9" r="0.9" fill="#fff"/></svg>',
        info:
          '<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="8" fill="currentColor"/><path d="M9 8.2v4" stroke="#fff" stroke-width="1.6" stroke-linecap="round"/><circle cx="9" cy="5.6" r="0.9" fill="#fff"/></svg>',
      };
    },
    containerHeight() {
      if (!this.toasts.length) return 0;
      if (this.expanded) {
        return this.toasts.reduce(
          (sum, t, i) => sum + this.heightOf(t.id) + (i > 0 ? GAP : 0),
          0
        );
      }
      const behind = Math.min(this.toasts.length - 1, MAX_VISIBLE - 1);
      return this.heightOf(this.toasts[0].id) + behind * PEEK;
    },
  },
  watch: {
    toasts: {
      immediate: true,
      handler(toasts) {
        const ids = new Set(toasts.map((t) => t.id));
        toasts.forEach((t) => this.ensureTimer(t));
        Object.keys(this.timers).forEach((id) => {
          if (!ids.has(Number(id))) {
            clearTimeout(this.timers[id].handle);
            delete this.timers[id];
          }
        });
      },
    },
  },
  mounted() {
    this.measure();
  },
  updated() {
    this.measure();
  },
  beforeDestroy() {
    Object.values(this.timers).forEach((t) => clearTimeout(t.handle));
  },
  methods: {
    dismiss: dismissToast,
    heightOf(id) {
      return this.heights[id] || FALLBACK_HEIGHT;
    },
    itemStyle(index) {
      const style = { zIndex: this.toasts.length - index };
      if (this.expanded) {
        let y = 0;
        for (let i = 0; i < index; i++) {
          y += this.heightOf(this.toasts[i].id) + GAP;
        }
        style["--y"] = `${y}px`;
        style["--s"] = 1;
        style["--o"] = 1;
      } else {
        style["--y"] = `${index * PEEK}px`;
        style["--s"] = 1 - index * 0.05;
        style["--o"] = index < MAX_VISIBLE ? 1 : 0;
        if (index >= MAX_VISIBLE) style.pointerEvents = "none";
      }
      return style;
    },
    measure() {
      const items = this.$refs.items || [];
      let changed = false;
      const heights = { ...this.heights };
      items.forEach((el) => {
        const id = el.dataset.toastId;
        if (el.offsetHeight && heights[id] !== el.offsetHeight) {
          heights[id] = el.offsetHeight;
          changed = true;
        }
      });
      if (changed) this.heights = heights;
    },
    ensureTimer(toast) {
      if (this.timers[toast.id] || !toast.timeout) return;
      this.timers[toast.id] = {
        remaining: toast.timeout,
        startedAt: Date.now(),
        handle: this.expanded ? null : setTimeout(() => dismissToast(toast.id), toast.timeout),
      };
    },
    pauseAll() {
      this.expanded = true;
      Object.values(this.timers).forEach((t) => {
        if (t.handle === null) return;
        clearTimeout(t.handle);
        t.remaining = Math.max(t.remaining - (Date.now() - t.startedAt), 0);
        t.handle = null;
      });
    },
    resumeAll() {
      this.expanded = false;
      Object.entries(this.timers).forEach(([id, t]) => {
        if (t.handle !== null) return;
        t.startedAt = Date.now();
        // give just-expired toasts a beat so they don't vanish mid-mouseleave
        t.handle = setTimeout(() => dismissToast(Number(id)), Math.max(t.remaining, 600));
      });
    },
  },
};
</script>

<style scoped>
.ash-toaster {
  position: fixed;
  top: 16px;
  right: 16px;
  width: 360px;
  max-width: calc(100vw - 32px);
  z-index: 1200;
  transition: height 0.35s cubic-bezier(0.21, 1.02, 0.73, 1);
}

.ash-toaster-stack {
  position: relative;
  width: 100%;
  height: 100%;
}

.ash-toast {
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 13px 14px;
  background: #fff;
  border: 1px solid #e9ebee;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(17, 24, 39, 0.1);
  transform: translate3d(0, var(--y), 0) scale(var(--s));
  transform-origin: top center;
  opacity: var(--o);
  transition: transform 0.35s cubic-bezier(0.21, 1.02, 0.73, 1), opacity 0.25s ease;
}

.ash-toast-enter {
  transform: translate3d(calc(100% + 24px), var(--y), 0) scale(var(--s));
  opacity: 0;
}

.ash-toast-leave-active {
  pointer-events: none;
}

.ash-toast-leave-to {
  transform: translate3d(calc(100% + 24px), var(--y), 0) scale(var(--s));
  opacity: 0;
}

.ash-toast-icon {
  flex-shrink: 0;
  display: inline-flex;
  margin-top: 1px;
}

.ash-toast-icon.is-success {
  color: #019256;
}

.ash-toast-icon.is-error {
  color: #dc3545;
}

.ash-toast-icon.is-warning {
  color: #f59e0b;
}

.ash-toast-icon.is-info {
  color: #3b82f6;
}

.ash-toast-content {
  flex: 1;
  min-width: 0;
}

.ash-toast-title {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  line-height: 1.4;
}

.ash-toast-message {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.45;
  word-wrap: break-word;
}

.ash-toast-message.no-title {
  color: #111827;
}

.ash-toast-message >>> p {
  margin: 0;
}

.ash-toast-close {
  position: absolute;
  top: -6px;
  right: -6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: #fff;
  border: 1px solid #e9ebee;
  border-radius: 50%;
  color: #6b7280;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease, color 0.15s ease;
}

.ash-toast:hover .ash-toast-close {
  opacity: 1;
}

.ash-toast-close:hover {
  color: #111827;
}
</style>
